// AI assisted development
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '@/services/api';
import { Term } from '@/types';

export default function Terms() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [termToDelete, setTermToDelete] = useState<Term | null>(null);

  const [term, setTerm] = useState('');
  const [definition, setDefinition] = useState('');
  const [displayOrder, setDisplayOrder] = useState(0);

  const { data: terms, isLoading, error } = useQuery({
    queryKey: ['terms'],
    queryFn: () => api.getTerms(),
  });

  const createMutation = useMutation({
    mutationFn: (t: Partial<Term>) => api.createTerm(t),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms'] });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (t: Term) => api.updateTerm(t),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms'] });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTerm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms'] });
      setDeleteDialogOpen(false);
      setTermToDelete(null);
    },
  });

  const handleOpenDialog = (t?: Term) => {
    if (t) {
      setEditingTerm(t);
      setTerm(t.term);
      setDefinition(t.definition);
      setDisplayOrder(t.displayOrder);
    } else {
      setEditingTerm(null);
      setTerm('');
      setDefinition('');
      setDisplayOrder(0);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTerm(null);
    setTerm('');
    setDefinition('');
    setDisplayOrder(0);
  };

  const handleSave = () => {
    const payload = { term, definition, displayOrder };
    if (editingTerm) {
      updateMutation.mutate({ ...editingTerm, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDeleteClick = (t: Term) => {
    setTermToDelete(t);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading terms: {(error as Error).message}
      </Alert>
    );
  }

  const sortedTerms = Array.isArray(terms) ? [...terms].sort((a, b) => a.displayOrder - b.displayOrder) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Terms (Glossary)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Term
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Terms are used to replace acronyms or phrases in Jira titles and descriptions when data is received.
        For example, add &quot;API&quot; with definition &quot;Application Programming Interface&quot;.
        Replacements are applied locally only; Jira is not updated.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Term</TableCell>
              <TableCell>Definition</TableCell>
              <TableCell align="right">Order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTerms.length > 0 ? (
              sortedTerms.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.term}</TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>{t.definition}</TableCell>
                  <TableCell align="right">{t.displayOrder}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(t)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(t)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No terms configured</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTerm ? 'Edit Term' : 'Add Term'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Term"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              fullWidth
              helperText="Acronym or phrase to find (e.g. API, SSO)"
            />
            <TextField
              label="Definition"
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              fullWidth
              multiline
              rows={2}
              helperText="Text to substitute when the term is found"
            />
            <TextField
              label="Display Order"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
              sx={{ width: 150 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending || !term.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the term <strong>{termToDelete?.term}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => termToDelete && deleteMutation.mutate(termToDelete.id)}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
